import type { AppVersion } from "@/api/hooks";

/** The build this page load runs, and whether to offer a reload.
 *
 *  ``GET /api/version`` describes the SERVER — ``useServerStatusPoller``
 *  re-asks every 20 s — and a deploy moves the server on while an open
 *  tab keeps running the old bundle (old UI, old lazy chunks). So the
 *  navbar pill and the release-notes card show the tab's own build, and a
 *  server that runs another one offers a reload.
 *
 *  The tab's build is baked into the bundle: CI passes ``COMMIT_SHA`` to
 *  docker/frontend.Dockerfile, which builds with ``VITE_COMMIT_SHA`` and
 *  ``VITE_APP_VERSION`` (the repo's ``VERSION``). Round 1 took the first
 *  ``/api/version`` answer instead, which was wrong during a deploy: a
 *  tab loaded while the old backend still answered (the migrate window)
 *  runs the NEW bundle but recorded the old version, and a tab that
 *  loaded a failed release's bundle heard only the rolled-back backend
 *  and never offered the reload. The first answer is now only the
 *  fallback for a bundle built without the build arg (``npm run dev``,
 *  vitest, a bare local ``docker build``).
 *
 *  Module state on purpose: it lives exactly as long as the loaded
 *  bundle, and a client-side route change keeps it. */
export type BuildId = Pick<AppVersion, "version" | "commit_sha" | "built_at">;

/** The tab's build and where it came from: baked into the bundle, or the
 *  first server answer of this page load. */
export type TabBuild = { build: BuildId; baked: boolean };

/** ``sessionStorage`` key: the server commit this tab last reloaded for. */
export const RELOADED_FOR_KEY = "rw.reloadedFor";

let firstAnswer: BuildId | null = null;

/** A commit id worth comparing: not empty, not the ``unknown`` a build
 *  without the ``COMMIT_SHA`` arg reports. */
function knownSha(sha: string | null | undefined): string | null {
  const value = (sha ?? "").trim().toLowerCase();
  return value && value !== "unknown" ? value : null;
}

/** The same commit, spelled at any length (the backend reports 12 hex
 *  digits; a local build may pass the full id or a 7-digit one). */
function sameCommit(a: string, b: string): boolean {
  return a.startsWith(b) || b.startsWith(a);
}

/** The identity CI baked into this bundle, or ``null`` when it was built
 *  without one. Read on every call so tests can stub the env. */
export function bakedBuild(): BuildId | null {
  // Spelled out per key: Vite replaces each one with its string at build
  // time (``import.meta.env`` as a whole would inline the whole object).
  const sha = knownSha(import.meta.env.VITE_COMMIT_SHA);
  if (!sha) return null;
  return {
    version: (import.meta.env.VITE_APP_VERSION ?? "").trim(),
    commit_sha: sha,
    built_at: (import.meta.env.VITE_BUILT_AT ?? "").trim(),
  };
}

/** Keep ``answer`` as the fallback build unless one is already known. */
export function noteServerBuild(answer: BuildId): void {
  firstAnswer ??= {
    version: answer.version,
    commit_sha: answer.commit_sha,
    built_at: answer.built_at,
  };
}

/** The build this tab runs; ``null`` for an unbaked bundle until the
 *  first ``/api/version`` answer of this page load. */
export function tabBuild(): TabBuild | null {
  const baked = bakedBuild();
  if (baked) return { build: baked, baked: true };
  return firstAnswer ? { build: firstAnswer, baked: false } : null;
}

/** ``tabBuild()`` without its provenance — what the pill shows. */
export function loadedBuild(): BuildId | null {
  return tabBuild()?.build ?? null;
}

/** Same bundle: a rebuild of the same version is another one. */
export function isSameBuild(a: BuildId, b: BuildId): boolean {
  return a.version === b.version && a.commit_sha === b.commit_sha;
}

/** Should a tab running ``tab`` offer a reload to reach ``server``?
 *
 *  * Baked bundle: when the server names a commit and it is another one —
 *    unless this tab already reloaded once for that server commit
 *    (``reloadedFor``). The reload brings back whatever bundle the edge
 *    serves; when BACKEND_TAG and FRONTEND_TAG are deliberately pinned to
 *    different releases that is the same bundle, and asking again after
 *    every reload would never end.
 *  * Unbaked bundle: the server's build differs from the first answer of
 *    this page load (round 1's rule). */
export function offersReloadTo(
  server: BuildId,
  tab: TabBuild | null,
  reloadedFor: string | null,
): boolean {
  if (!tab) return false;
  if (!tab.baked) return !isSameBuild(server, tab.build);
  const serverSha = knownSha(server.commit_sha);
  const tabSha = knownSha(tab.build.commit_sha);
  if (!serverSha || !tabSha || sameCommit(serverSha, tabSha)) return false;
  const tookIt = knownSha(reloadedFor);
  return !(tookIt && sameCommit(tookIt, serverSha));
}

/** The server commit this tab last reloaded for (``reloadForServerBuild``). */
export function reloadedForSha(): string | null {
  try {
    return knownSha(window.sessionStorage.getItem(RELOADED_FOR_KEY));
  } catch {
    return null;
  }
}

/** Load the bundle the server runs now (``index.html`` is served
 *  ``no-cache``, so a plain reload picks it up), remembering which server
 *  commit it was for so the same offer is not made again. */
export function reloadForServerBuild(serverSha: string | null | undefined): void {
  const sha = knownSha(serverSha);
  if (sha) {
    try {
      window.sessionStorage.setItem(RELOADED_FOR_KEY, sha);
    } catch {
      // Storage blocked: the offer comes back after the reload, which
      // still loads the bundle the edge serves now.
    }
  }
  window.location.reload();
}

/** Tests only: start over as if the page had just loaded. */
export function forgetLoadedBuild(): void {
  firstAnswer = null;
}
